import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { login as apiLogin, fetchMe, googleSession, logoutApi, verifyTotp } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = logged
  const [ready, setReady] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      setUser(await fetchMe());
    } catch {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // Ritorno da Google OAuth: la sessione la stabilisce AuthCallback
    if (window.location.hash?.includes("session_id=")) {
      setUser(false);
      setReady(true);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Step 1: credenziali → ritorna { mfa_required | setup_required, mfa_token, qr... } senza aprire sessione
  const signIn = useCallback(async (identifier, password) => apiLogin(identifier, password), []);

  // Step 2: codice Google Authenticator → apre la sessione
  const confirmTotp = useCallback(async (mfaToken, code) => {
    const data = await verifyTotp(mfaToken, code);
    setUser(data.user);
    return data.user;
  }, []);

  const signInGoogle = useCallback(async (sessionId) => {
    const data = await googleSession(sessionId);
    setUser(data.user);
    return data.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await logoutApi();
    } catch (err) {
      console.warn("Logout server non riuscito (sessione già scaduta?)", err?.response?.status);
    }
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, ready, signIn, confirmTotp, signInGoogle, signOut }),
    [user, ready, signIn, confirmTotp, signInGoogle, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
